import React, { FC } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlineOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ToolTip } from '../../../../../components/Tooltip/ToolTip';
import { Cancel, CheckCircle } from '@mui/icons-material';

export interface KycDocument {
  id: number;
  bookingId: number;
  documentType: string;
  isReceived?: boolean;
}

interface KycDocumentsTableProps {
  handleDelete?: (id: number) => void;
  handleEdit: (id: number) => void;
  documents: KycDocument[];
}

const KycDocumentsTable: FC<KycDocumentsTableProps> = ({ handleDelete, handleEdit, documents }) => {
  const theme = useTheme();

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        KYC Documents
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.primary.main,
                '& .MuiTableCell-root': {
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  border: `1px solid ${theme.palette.primary.main}`,
                },
              }}>
              <TableCell>Document Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((document, index) => (
              <TableRow
                key={document.id}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}>
                <TableCell sx={{ py: 0.5, px: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {document.isReceived ? (
                      <Tooltip title="Received">
                        <CheckCircle sx={{ color: 'success.main' }} fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Not Received">
                        <Cancel sx={{ color: 'error.main' }} fontSize="small" />
                      </Tooltip>
                    )}
                    <Typography variant="body2" noWrap>
                      {document.documentType}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell align="right" sx={{ textAlign: 'right' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <ToolTip title="Edit Document">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit?.(index)}
                        sx={{ color: theme.palette.info.main }}>
                        <EditOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </ToolTip>

                    <ToolTip title="Delete Document">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete?.(index)}
                        sx={{ color: theme.palette.error.main }}>
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </ToolTip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default KycDocumentsTable;
